// A silent window proves only that THIS client heard nothing; the daemon's status decides (task_2ae721dc).
import { workspaceState } from "../../core/state.svelte.js";
import { agentActivityStore } from "./agent-activity-store.svelte.js";
import { turnStatusBook } from "./turn-status.svelte.js";
import type { StreamEntry } from "./history/types.js";

export type IdleTurnVerdict = "alive" | "ended" | "silent";

export interface IdleTurnFacts {
  daemonOffline: boolean;
  // Offline AND seen dropping by this client inside the redial window, read now (DaemonState.isReconnecting).
  daemonReconnecting: boolean;
  daemonLifecycle: string | null | undefined;
  toolInFlight: boolean;
  backgroundTasks: number;
  spared: number;
}

// ponytail: a daemon stuck on "running" (CYBORG-673) is still called after 4 silent windows
// (1 h); raise it if a real tool runs silent longer.
export const MAX_SPARED_WINDOWS = 4;

export function judgeIdleTurn(f: IdleTurnFacts): IdleTurnVerdict {
  if (f.daemonOffline) return "silent";
  if (f.daemonLifecycle === "idle") return "ended";
  if (f.daemonLifecycle && f.daemonLifecycle !== "running") return "silent";
  if (f.spared >= MAX_SPARED_WINDOWS) return "silent";
  if (f.daemonLifecycle === "running") return "alive";
  return f.toolInFlight || f.backgroundTasks > 0 ? "alive" : "silent";
}

// Why a turn was called silent, for the report. Only a daemon with redial proof is transient; an
// offline one without it, and every other reason, is a fault.
export type IdleTurnSilence =
  | "daemon_reconnecting"
  | "daemon_offline_unproven"
  | "agent_not_running"
  | "spare_ceiling"
  | "no_daemon_word";

export function silenceReason(f: IdleTurnFacts): IdleTurnSilence {
  if (f.daemonOffline)
    return f.daemonReconnecting ? "daemon_reconnecting" : "daemon_offline_unproven";
  if (f.daemonLifecycle && f.daemonLifecycle !== "running") return "agent_not_running";
  return f.spared >= MAX_SPARED_WINDOWS ? "spare_ceiling" : "no_daemon_word";
}

const TERMINAL_TOOL = new Set(["completed", "failed", "canceled"]);

// ponytail: status is per agent: busy in another session, it keeps a dead turn alive up to 1 h; upgrade: per-session turn status.
export function idleTurnFacts(
  agentId: string,
  entries: readonly StreamEntry[],
  spared: number,
  isKnownOffline: (daemonId: string) => boolean,
  isReconnecting: (daemonId: string) => boolean,
): IdleTurnFacts {
  const row = workspaceState.agents.find((a) => a.agentId === agentId);
  const status = turnStatusBook.current(agentId);
  return {
    daemonOffline: !!row?.daemonId && isKnownOffline(row.daemonId),
    daemonReconnecting: !!row?.daemonId && isReconnecting(row.daemonId),
    daemonLifecycle: status?.lifecycle ?? row?.lifecycle,
    toolInFlight: entries.some((e) => e.kind === "tool_call" && !TERMINAL_TOOL.has(e.status)),
    backgroundTasks: agentActivityStore.forAgent(agentId).tasks.length,
    spared,
  };
}
